/**
 * Client for the signed-in user's own call history (/calls on the backend;
 * see calls.py). Pages are keyed by the last call ID of the previous page.
 */
import { auth } from './firebase';
import { BACKEND_URL } from './developerApi';
import { CountryCode } from './market';
import { fmtDateTime, fmtTime, zoneForCountry } from './timeFormat';

export interface CallSummary {
  callId: string;
  /** First line of what was asked, else the objective, else "Call to <number>". */
  title: string;
  toNumber: string | null;
  toCountry: string | null;
  status: string | null;
  /** 'machine' or 'fax' when a machine picked up. */
  answeredBy: string | null;
  duration: number | null;
  initiatedAt: string | null;
  completedAt: string | null;
  creditDeducted: boolean;
  hasTranscript: boolean;
}

export interface CallDetail extends CallSummary {
  fromNumber: string | null;
  source: string | null;
  /** 'ja' or 'en'; absent on calls made before it was recorded. */
  language: 'ja' | 'en' | null;
  prompt: string | null;
  objective: string | null;
  creditsDeducted: number | null;
  /** Why no credit was charged: 'no_conversation' or 'insufficient_credits'. */
  reason: string | null;
  /** The call's length limit: 240 (one credit) or up to 480 (a second credit if it ran past 240). */
  maxDurationSeconds: number | null;
  /** The bot had to hang up at that limit, so the call may not have finished its business. */
  timeLimitReached: boolean;
}

export interface TranscriptMessage {
  // 'event' marks a non-speech sound the bot detected: a beep or a fax tone,
  // or something the bot did: hung up at the time limit ('time_limit').
  role: 'user' | 'assistant' | 'event';
  /** Which event, for role 'event'. */
  event?: string;
  content: string;
  timestamp?: string;
}

export interface CallPage {
  calls: CallSummary[];
  nextCursor: string | null;
}

export const PAGE_SIZE = 20;

async function callsFetch<T>(path: string, method = 'GET'): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Please sign in again.');
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export function listCalls(cursor: string | null = null, limit = PAGE_SIZE): Promise<CallPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return callsFetch<CallPage>(`/calls?${params}`);
}

export function getCall(callId: string): Promise<CallDetail> {
  return callsFetch<CallDetail>(`/calls/${encodeURIComponent(callId)}`);
}

export async function getCallTranscript(callId: string): Promise<TranscriptMessage[]> {
  const data = await callsFetch<{ transcript: TranscriptMessage[] }>(
    `/calls/${encodeURIComponent(callId)}/transcript`
  );
  return data.transcript;
}

/**
 * Delete one of the user's calls. It is gone from the history right away; its
 * recording and transcript are erased after a 24-hour review (calls.py). A
 * call still in progress is refused (409).
 */
export async function deleteCall(callId: string): Promise<void> {
  await callsFetch<unknown>(`/calls/${encodeURIComponent(callId)}`, 'DELETE');
}

/** A call's destination for display purposes; older calls only have the number. */
export interface CallPlace {
  toCountry: string | null;
  toNumber: string | null;
}

/** The country the call went to; Canada is dialed like the US. */
export function callCountry(call: CallPlace): CountryCode {
  if (call.toCountry === 'JP' || call.toNumber?.startsWith('+81')) return 'JP';
  return 'US';
}

/**
 * A call's times are in Japan time for calls to Japan, whichever zone the
 * viewer is in, and in the viewer's own zone for calls to the US and Canada.
 */
export function fmtCallDateTime(iso: string | null, call: CallPlace): string {
  return fmtDateTime(iso, zoneForCountry(callCountry(call)));
}

export function fmtCallTime(iso: string, call: CallPlace): string {
  return fmtTime(iso, zoneForCountry(callCountry(call)));
}
